/**
 * Ablauf nach dem Angebot (Wunsch von Mathias, 11/2026): Schritt für Schritt,
 * mit Zeitstempel und wer es war, mit Erinnerung, und mit den Dokumenten, die
 * dazugehören — der Netzzugangsvertrag wird beim Hochladen ausgelesen, die
 * Fertigmeldung braucht die Datei des Wechselrichters.
 */

import { useState } from 'react';

import {
  DOKUMENTARTEN,
  faelligText,
  projektlage,
  schrittstaende,
  type Erinnerungsvorgabe,
  type Schrittstand,
} from '../abwicklung.ts';
import { speichere } from '../daten/db.ts';
import { tagVon } from '../datum.ts';
import {
  dokumentdatei,
  entferneDokument,
  hakeAb,
  legeDokumentAb,
  oeffneDokument,
  setzeSchritt,
} from '../daten/schritte.ts';
import type { Vertragsfund } from '../export/vertrag.ts';
import type { Angebotszeile, Auftragszeile, Dokument, Dokumentart, Projekt, Projektschritt } from '../typen.ts';

interface Eigenschaften {
  projekt: Projekt;
  angebote: Angebotszeile[];
  auftraege: Auftragszeile[];
  schritte: Projektschritt[];
  dokumente: Dokument[];
  vorgabe: Erinnerungsvorgabe;
  /** Montage: Projektfelder ändert sie nicht — das nimmt der Server nicht an. */
  nurLesen?: boolean;
}

const datum = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('de-AT') : '');

const DOKUMENTNAME = new Map(DOKUMENTARTEN);

export function Ablaufpanel(e: Eigenschaften): React.JSX.Element {
  const heute = new Date();
  const staende = schrittstaende({
    angebote: e.angebote,
    auftraege: e.auftraege,
    schritte: e.schritte,
    dokumente: e.dokumente.filter((d) => !d.geloescht_am),
    vorgabe: e.vorgabe,
    heute,
  });
  const lage = projektlage(staende);
  const [meldung, setMeldung] = useState<{ text: string; fehler?: boolean } | null>(null);
  const [fund, setFund] = useState<{ dokument: string; fund: Vertragsfund } | null>(null);
  const [liest, setLiest] = useState<string | null>(null);

  const dokumenteZu = (art: Dokumentart): Dokument[] => e.dokumente.filter((d) => d.art === art && !d.geloescht_am);

  async function liesVertrag(dok: Dokument, blob?: Blob): Promise<void> {
    setLiest(dok.id);
    setFund(null);
    try {
      if (!(dok.mime ?? '').includes('pdf')) throw new Error('Auslesen geht nur bei PDF-Dateien.');
      const { textAusPdf, lieseVertrag } = await import('../export/vertrag.ts');
      const daten = new Uint8Array(await (blob ?? (await dokumentdatei(dok))).arrayBuffer());
      setFund({ dokument: dok.id, fund: lieseVertrag(await textAusPdf(daten)) });
    } catch (f) {
      setMeldung({ text: `Nicht ausgelesen: ${f instanceof Error ? f.message : String(f)}`, fehler: true });
    } finally {
      setLiest(null);
    }
  }

  async function ladeHoch(art: Dokumentart, datei: File | undefined): Promise<void> {
    if (!datei) return;
    if (datei.size > 25 * 1024 * 1024) {
      setMeldung({ text: 'Die Datei ist größer als 25 MB.', fehler: true });
      return;
    }
    let dok: Dokument;
    try {
      dok = await legeDokumentAb(e.projekt.id, art, datei);
    } catch (f) {
      // Voller Speicher am Gerät, meistens — still scheitern hieß, die Datei
      // gilt als abgelegt und ist es nicht.
      setMeldung({ text: `„${datei.name}“ nicht abgelegt: ${f instanceof Error ? f.message : String(f)}`, fehler: true });
      return;
    }
    setMeldung({
      text: `„${datei.name}“ abgelegt${navigator.onLine ? '' : ' — geht mit dem nächsten Abgleich hoch'}.`,
    });
    if (art === 'netzzugangsvertrag') await liesVertrag(dok, datei);
  }

  // Gewöhnliche Funktionen, keine Komponenten: Als Komponente im Panel würde
  // React sie bei jedem Abgleich neu aufbauen, und ein Datumsfeld verlöre
  // mitten in der Eingabe den Fokus.
  function dokumentliste(art: Dokumentart): React.JSX.Element {
    const liste = dokumenteZu(art);
    return (
      <div className="ablauf__dokumente" key={`dok-${art}`}>
        {liste.map((d) => (
          <span key={d.id} className="ablauf__dokument">
            <button className="schlicht" onClick={() => void oeffneDokument(d).catch((f) => setMeldung({ text: String(f), fehler: true }))}>
              {d.dateiname ?? d.titel ?? 'Dokument'}
            </button>
            {d.hochgeladen === false && <span className="klein gedaempft">wartet auf Abgleich</span>}
            {art === 'netzzugangsvertrag' && (
              <button className="schlicht" disabled={liest === d.id} onClick={() => void liesVertrag(d)}>
                {liest === d.id ? 'Liest …' : 'Auslesen'}
              </button>
            )}
            <button
              className="schlicht gefaehrlich"
              aria-label={`${d.dateiname ?? 'Dokument'} entfernen`}
              onClick={() => {
                if (confirm(`„${d.dateiname ?? 'Dokument'}“ entfernen?`)) void entferneDokument(d);
              }}
            >
              ×
            </button>
          </span>
        ))}
        <label className="knopf ablauf__hochladen">
          {liste.length > 0 ? 'Weitere Datei' : `${DOKUMENTNAME.get(art)} hochladen`}
          <input
            type="file"
            hidden
            onChange={(ereignis) => {
              void ladeHoch(art, ereignis.target.files?.[0]);
              ereignis.target.value = '';
            }}
          />
        </label>
      </div>
    );
  }

  function zeile(s: Schrittstand): React.JSX.Element {
    const vonHand = s.quelle === 'hand';
    const optional = s.schritt === 'netzzugangsvertrag' || s.schritt === 'stromliefervertrag' || s.schritt === 'foerderansuchen';
    return (
      <li key={s.schritt} className={`ablauf__schritt ablauf__schritt--${s.zustand}`}>
        <div className="ablauf__kopf">
          <span className="ablauf__titel">{s.titel}</span>
          <span className="klein">
            {s.zustand === 'erledigt' &&
              `erledigt ${datum(s.erledigtAm)}${s.erledigtVon ? ` · ${s.erledigtVon}` : ''}${s.quelle === 'abgeleitet' ? ' · aus dem Projekt' : ''}`}
            {s.zustand === 'entfaellt' && (s.zeile?.entfaellt ? 'entfällt' : 'ohne — der Auftrag ist schon da')}
            {(s.zustand === 'offen' || s.zustand === 'faellig') && (faelligText(s, heute) ?? 'offen')}
            {s.zustand === 'wartet' && 'kommt später'}
          </span>
        </div>
        {s.fehlt && s.zustand !== 'erledigt' && s.zustand !== 'wartet' && (
          <div className="klein ablauf__fehlt">{s.fehlt}</div>
        )}
        {s.zeile?.notiz && <div className="klein gedaempft">{s.zeile.notiz}</div>}

        <div className="knopfreihe ablauf__aktionen">
          {(s.zustand === 'offen' || s.zustand === 'faellig' || s.zustand === 'wartet') && (
            <button disabled={!!s.fehlt} title={s.fehlt ?? undefined} onClick={() => void hakeAb(e.projekt.id, s.schritt)}>
              Erledigt
            </button>
          )}
          {optional && (s.zustand === 'offen' || s.zustand === 'faellig' || s.zustand === 'wartet') && (
            <button className="schlicht" onClick={() => void setzeSchritt(e.projekt.id, s.schritt, { entfaellt: true })}>
              Entfällt
            </button>
          )}
          {(vonHand || s.zeile?.entfaellt) && (
            <button
              className="schlicht"
              onClick={() =>
                void setzeSchritt(e.projekt.id, s.schritt, { erledigt_am: null, erledigt_von: null, entfaellt: false })
              }
            >
              Zurücknehmen
            </button>
          )}
          {(s.zustand === 'offen' || s.zustand === 'faellig' || s.zustand === 'wartet') && s.schritt !== 'angebot' && (
            <label className="klein ablauf__erinnerung">
              Erinnern am
              <input
                type="date"
                key={`${s.schritt}-${s.erinnernAm ?? ''}`}
                defaultValue={s.erinnernAm ?? ''}
                onBlur={(ereignis) => {
                  const wert = ereignis.target.value || null;
                  if (wert !== tagVon(s.zeile?.erinnern_am)) void setzeSchritt(e.projekt.id, s.schritt, { erinnern_am: wert });
                }}
              />
            </label>
          )}
        </div>

        {s.dokument && s.zustand !== 'entfaellt' && dokumentliste(s.dokument)}
        {s.schritt === 'fertigmeldung' && s.zustand !== 'entfaellt' && dokumentliste('wechselrichter')}

        {s.schritt === 'netzzugangsvertrag' && fund && (
          <Vertragsvorschlag projekt={e.projekt} fund={fund.fund} nurLesen={!!e.nurLesen} onSchliessen={() => setFund(null)} onMeldung={setMeldung} />
        )}
      </li>
    );
  }

  return (
    <section>
      <h2>Ablauf</h2>
      <dl className="zahlenblock">
        <div>
          <dt>Angebot</dt>
          <dd>{lage.angebotVersendet.zustand === 'erledigt' ? `versendet ${datum(lage.angebotVersendet.erledigtAm)}` : 'noch nicht'}</dd>
        </div>
        <div>
          <dt>Auftrag</dt>
          <dd>{lage.auftragErhalten.zustand === 'erledigt' ? `erhalten ${datum(lage.auftragErhalten.erledigtAm)}` : 'noch nicht'}</dd>
        </div>
      </dl>
      {meldung && <div className={meldung.fehler ? 'hinweis hinweis--fehler' : 'hinweis hinweis--gut'}>{meldung.text}</div>}
      <ol className="ablauf">
        {staende.map((s) => zeile(s))}
      </ol>
      <h3>Weitere Dokumente</h3>
      {dokumentliste('sonstiges')}
    </section>
  );
}

/**
 * Was im Netzzugangsvertrag gefunden wurde, mit der Fundstelle — übernommen
 * wird erst auf Tipp, und nur, was sich vom Projekt unterscheidet.
 */
function Vertragsvorschlag({
  projekt,
  fund,
  nurLesen,
  onSchliessen,
  onMeldung,
}: {
  projekt: Projekt;
  fund: Vertragsfund;
  nurLesen: boolean;
  onSchliessen: () => void;
  onMeldung: (m: { text: string; fehler?: boolean }) => void;
}): React.JSX.Element {
  if (!fund.textGefunden) {
    return (
      <div className="hinweis hinweis--warnung">
        Im Dokument steht kein lesbarer Text — vermutlich ein Scan. Zählpunkt und Einspeisebegrenzung bitte von Hand
        eintragen.
        <button className="schlicht" onClick={onSchliessen}>
          Schließen
        </button>
      </div>
    );
  }
  const zeilen: Array<{ feld: string; neu: string; alt: string | null; stelle: string; uebernimm: () => Promise<void> }> = [];
  if (fund.zaehlpunkt) {
    zeilen.push({
      feld: 'Zählpunkt',
      neu: fund.zaehlpunkt.wert,
      alt: projekt.zaehlpunktnummer,
      stelle: fund.zaehlpunkt.stelle,
      uebernimm: () => speichere('projekte', projekt, { zaehlpunktnummer: fund.zaehlpunkt!.wert }),
    });
  }
  if (fund.einspeisebegrenzungKw) {
    zeilen.push({
      feld: 'Einspeisebegrenzung',
      neu: `${fund.einspeisebegrenzungKw.wert.toLocaleString('de-AT')} kW`,
      alt: projekt.einspeisebegrenzung_kw != null ? `${projekt.einspeisebegrenzung_kw.toLocaleString('de-AT')} kW` : null,
      stelle: fund.einspeisebegrenzungKw.stelle,
      uebernimm: () => speichere('projekte', projekt, { einspeisebegrenzung_kw: fund.einspeisebegrenzungKw!.wert }),
    });
  }
  return (
    <div className="hinweis vertragsfund">
      <strong>Aus dem Netzzugangsvertrag</strong>
      {zeilen.length === 0 && <p className="klein">Weder Zählpunkt noch Einspeisebegrenzung erkannt.</p>}
      {!fund.einspeisebegrenzungKw && zeilen.length > 0 && (
        <p className="klein">Keine Einspeisebegrenzung erkannt — das heißt nicht, dass es keine gibt.</p>
      )}
      {zeilen.map((z) => (
        <div key={z.feld} className="vertragsfund__zeile">
          <div>
            {z.feld}: <strong>{z.neu}</strong>
            {z.alt === z.neu ? (
              <span className="klein gedaempft"> — steht schon so im Projekt</span>
            ) : z.alt ? (
              <span className="klein"> — im Projekt steht {z.alt}</span>
            ) : null}
          </div>
          <div className="klein gedaempft">„…{z.stelle}…“</div>
          {z.alt !== z.neu && !nurLesen && (
            <button
              onClick={() =>
                void z.uebernimm().then(() => onMeldung({ text: `${z.feld} übernommen: ${z.neu}.` }))
              }
            >
              Übernehmen
            </button>
          )}
        </div>
      ))}
      <button className="schlicht" onClick={onSchliessen}>
        Schließen
      </button>
    </div>
  );
}
